import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * The seam between two panes, made draggable.
 *
 * Pointer events rather than mouse events, so a trackpad, a pen and a touch
 * screen all work from one code path, and pointer capture rather than
 * window-level listeners, so a fast drag that leaves the 6px target does not
 * drop the gesture.
 *
 * It is a `separator` with arrow keys as well as a drag: the width of the pane
 * you write prompts in is a real preference, and a preference reachable only by
 * dragging a hairline is not reachable by everyone.
 */
export function PaneResizer({
  label,
  width,
  min,
  max,
  /** Which side of the handle the pane being sized is on. */
  edge,
  onChange,
}: {
  label: string;
  width: number;
  min: number;
  max: number;
  edge: 'left' | 'right';
  onChange: Dispatch<SetStateAction<number>>;
}) {
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; width: number } | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(max, Math.max(min, Math.round(value))),
    [min, max],
  );

  // While a drag is live the cursor belongs to the drag, not to whatever it
  // happens to be passing over — otherwise it flickers into text carets over
  // the panes either side.
  useEffect(() => {
    if (!dragging) return;
    const previous = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = previous;
      document.body.style.userSelect = '';
    };
  }, [dragging]);

  return (
    <div
      className="pane-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      data-dragging={dragging}
      onDoubleClick={() => onChange(clamp(edge === 'left' ? 232 : 400))}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        start.current = { x: event.clientX, width };
        setDragging(true);
      }}
      onPointerMove={(event) => {
        if (!start.current) return;
        const delta = event.clientX - start.current.x;
        onChange(clamp(start.current.width + (edge === 'left' ? delta : -delta)));
      }}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        start.current = null;
        setDragging(false);
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 48 : 16;
        // From the previous width rather than the rendered one: a held arrow key
        // fires faster than React re-renders, and reading the prop would make
        // every repeat after the first a no-op.
        if (event.key === 'ArrowLeft') {
          onChange((previous) => clamp(previous + (edge === 'left' ? -step : step)));
        } else if (event.key === 'ArrowRight') {
          onChange((previous) => clamp(previous + (edge === 'left' ? step : -step)));
        } else return;
        event.preventDefault();
      }}
    />
  );
}

/**
 * A pane width that survives a reload.
 *
 * Layout preferences are per-browser, not per-account: they are about the
 * screen you are sitting at, so they live in `localStorage` rather than in the
 * config repo.
 */
export function usePaneWidth(
  key: string,
  fallback: number,
): [number, Dispatch<SetStateAction<number>>] {
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? stored : fallback;
  });

  useEffect(() => {
    localStorage.setItem(key, String(width));
  }, [key, width]);

  return [width, setWidth];
}
